import styled from 'styled-components/macro';
import { themeGet } from '@styled-system/theme-get';
import {
  space,
  color,
  flexbox,
  typography,
  SpaceProps,
  ColorProps,
  FlexProps,
  TypographyProps
} from 'styled-system';

export const StyledInputMount = styled.input.attrs({
  type: 'text',
  placeholder: '0',
  inputMode: 'decimal',
  maxLength: 18
})<{
  error?: string;
}>`
  font-size: 64px;
  text-align: center;
  transition: none;
  font-family: ${themeGet('fonts.input')};
  font-weight: ${themeGet('fontWeights.bold')};
  color: ${(props) =>
    props.error !== ''
      ? themeGet('colors.input.textError')
      : themeGet('colors.text.default')};

  &:not(:last-child) {
    margin-bottom: 48px;
  }
`;

export const StyledButtonWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  &:not(:last-child) {
    margin-bottom: 80px;
  }
`;

export const StyledQrCodeWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  &:not(:last-child) {
    margin-bottom: 80px;
  }
`;

export const StyledLabel = styled.label`
  display: block;
  font-weight: ${themeGet('fontWeights.semiBold')};
  font-size: ${themeGet('fontSizes.xs')}px;
  color: ${themeGet('colors.input.text')};

  &:not(:last-child) {
    margin-bottom: 6px;
  }
`;

export const StyledDescription = styled.label`
  display: block;
  font-weight: ${themeGet('fontWeights.regular')};
  font-size: ${themeGet('fontSizes.xs')}px;
  color: ${themeGet('colors.primary.extra')};

  &:not(:last-child) {
    margin-bottom: 64px;
  }
`;

type InputWrapperProps = FlexProps &
  SpaceProps & {
    alignItems?: 'center' | 'start' | 'flex-start' | 'flex-end';
  };

export const StyledInputWrapper = styled.div<InputWrapperProps>`
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  padding: 13px 20px;
  border-width: 1px;
  border-style: solid;
  border-color: ${themeGet('colors.text.border')};
  border-radius: ${themeGet('radii.small')};

  &.is--success {
    border-color: ${themeGet('colors.primary.regular')};

    .success-prefix {
      opacity: 1;
    }
  }

  &.is--error {
    border-color: ${themeGet('colors.input.textError')};
  }

  ${flexbox}
  ${space}
`;

export const StyledInputLabel = styled.span`
  font-weight: ${themeGet('fontWeights.semiBold')};
  font-size: ${themeGet('fontSizes.xs')}px;
  color: ${themeGet('colors.primary.extra')};
  line-height: 18px;
`;

type InputValueProps = FlexProps & TypographyProps & ColorProps & SpaceProps;

export const StyledInputValue = styled.div<InputValueProps>`
  flex: auto;
  text-align: right;
  font-weight: ${themeGet('fontWeights.regular')};
  font-size: ${themeGet('fontSizes.base')}px;
  color: ${themeGet('colors.primary.extra')};
  line-height: 18px;

  .success-prefix {
    position: absolute;
    pointer-events: none;
    top: 50%;
    transform: translateY(-50%);
    opacity: 0;
    margin-top: 1px;
    visibility: hidden;
    color: ${themeGet('colors.primary.regular')};
  }

  ${space}
  ${flexbox}
  ${typography}
  ${color}
`;

export const StyledInputButton = styled.button`
  display: flex;
  align-items: center;
  padding: 0;
  background-color: transparent;
  color: ${themeGet('colors.input.text')};
  font-weight: ${themeGet('fontWeights.semiBold')};
  font-size: ${themeGet('fontSizes.xxs')}px;

  > svg {
    margin-right: 3px;
  }

  &:hover {
    color: ${themeGet('colors.linkColor')};
  }
`;

export const StyledSummary = styled.div`
  overflow: hidden;
  border-width: 1px;
  border-style: solid;
  border-color: ${themeGet('colors.text.border')};
  border-radius: ${themeGet('radii.small')};

  &:not(:last-child) {
    margin-bottom: 20px;
  }
`;

export const StyledSummaryItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 20px;
`;

export const StyledSummaryLabel = styled.span<ColorProps>`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  font-weight: ${themeGet('fontWeights.semiBold')};
  font-size: ${themeGet('fontSizes.xs')}px;
  color: ${themeGet('colors.primary.extra')};
  line-height: 14px;

  p,
  span {
    font-size: ${themeGet('fontSizes.xs')}px;
    text-align: right;
  }

  p {
    color: ${themeGet('colors.primary.extra')};
  }

  span {
    color: rgba(139, 139, 139, 1);
  }

  ${color}
`;

export const StyledSummaryValue = styled.div`
  text-transform: uppercase;
  font-weight: ${themeGet('fontWeights.semi')};
  font-size: 20px;
  color: ${themeGet('colors.primary.extra')};
`;

export const StyledSummaryList = styled.div`
  display: flex;
  flex-direction: column;

  ${StyledSummaryItem} {
    background-color: #f8f7f7;

    &:not(:last-child) {
      border-bottom: 1px solid ${themeGet('colors.text.border')};
    }
  }
`;

export const StyledTitle = styled.h6`
  display: flex;
  justify-content: center;
  color: ${themeGet('colors.primary.extra')};
  font-weight: ${themeGet('fontWeights.semiBold')};

  &:not(:last-child) {
    margin-bottom: 24px;
  }
`;

export const StyledHeading = styled.h3`
  display: flex;
  justify-content: center;
  text-transform: uppercase;
  color: ${themeGet('colors.primary.extra')};
  font-weight: ${themeGet('fontWeights.semiBold')};

  &:not(:last-child) {
    margin-bottom: 76px;
  }
`;

export const StyledInfo = styled.h4`
  display: flex;
  justify-content: center;
  color: ${themeGet('colors.primary.extra')};
  font-weight: ${themeGet('fontWeights.semiBold')};

  > span {
    margin-left: 13px;
    text-transform: uppercase;
  }

  &:not(:last-child) {
    margin-bottom: 20px;
  }
`;

export const StyledMount = styled.div`
  display: flex;
  justify-content: center;
  font-size: 64px;
  font-family: ${themeGet('fonts.input')};
  font-weight: ${themeGet('fontWeights.bold')};
  color: ${themeGet('colors.text.default')};

  &:not(:last-child) {
    margin-bottom: 85px;
  }
`;

export const StyledCloseButton = styled.button`
  position: absolute;
  top: 18px;
  right: 21px;
  padding: 0;
  background-color: transparent;
`;

export const StyledContainer = styled.div`
  padding-top: 10px;
  padding-bottom: 10px;

  h4.styled-heading {
    margin-bottom: 15px;
    color: ${themeGet('colors.text.secondary')};
    font-weight: ${themeGet('fontWeights.semiBold')};
  }

  h6.styled-text {
    text-align: center;
    color: ${themeGet('colors.text.default')};
    font-weight: ${themeGet('fontWeights.regular')};
    line-height: 1.5;
  }
`;

export const StyledHolder = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 0 auto;
  width: min(328px, 100%);

  &:not(:last-child) {
    margin-bottom: 30px;
  }
`;

export const StyledCircleIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 123px;
  height: 123px;
  text-align: center;
  border-radius: 50%;
  background-color: ${themeGet('colors.text.secondary')};

  &:not(:last-child) {
    margin-bottom: 30px;
  }
`;
